import { useState, useEffect, Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Search, Plus, ChevronRight, Trash2, Pencil, Download, RefreshCw, Loader2, AlertTriangle, ExternalLink, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useConfirm } from '@/components/ConfirmDialog';
import { SearchableSelect } from '@/components/SearchableSelect';
import { AgencyDot, ViolationStatusDot } from '@/components/ViolationDots';
import { LinkExistingDialog } from '@/components/LinkExistingDialog';
import { inProject, buildProjectLinkPatch } from '@/lib/projectLink';

const AGENCIES = ['DOB', 'FDNY', 'DOT', 'DEP', 'ECB', 'HPD', 'Other'];
const STATUSES = ['Active', 'Resolved', 'Dismissed', 'Written Off', 'Pending'];

const FDNY_PORTAL_URL = 'https://fires.fdnycloud.org/';

// Pattern A = FDNY enforcement orders (portal only, not on NYC Open Data).
// Pattern B = ECB/OATH cases (numeric with R/L/H/N suffix), fetched via backend.
const fdnyPattern = (v) => {
  if (!v || v.issuing_agency !== 'FDNY') return null;
  const n = (v.violation_number || '').trim();
  if (/^(E\d+|DO\d+|.*ENFORC.*)$/i.test(n)) return 'A';
  if (/[RLHN]$/i.test(n)) return 'B';
  return 'A';
};

const EMPTY_FORM = {
  property_id: '', project_id: '', violation_number: '', issuing_agency: '', issue_date: '',
  description: '', remedy: '', penalty_amount: 0, compliance_status: 'Active',
  hearing_date: '', resolution_date: '', notes: '',
};

const fmtMoney = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ViolationsTab({ lockedPropertyId, lockedProjectId }) {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [violations, setViolations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [agencyFilter, setAgencyFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [propertyFilter, setPropertyFilter] = useState(lockedPropertyId || '');
  const [search, setSearch] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);

  // Link Existing (project scope only)
  const [linkOpen, setLinkOpen] = useState(false);
  const [linking, setLinking] = useState(false);

  // Inline row expansion state
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [refreshingId, setRefreshingId] = useState(null);
  const [detailWarning, setDetailWarning] = useState(null);

  const fmtDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString();
  };

  const fetchViolations = () => api.get('/violations').then(r => setViolations(Array.isArray(r.data) ? r.data : [])).catch(() => setViolations([]));
  const fetchSummary = () => api.get('/violations/summary').then(r => setSummary(r.data)).catch(() => setSummary(null));

  useEffect(() => {
    Promise.all([fetchViolations(), fetchSummary()]).finally(() => setLoading(false));
    api.get('/properties').then(r => setProperties(Array.isArray(r.data) ? r.data : [])).catch(() => setProperties([]));
    api.get('/projects').then(r => setProjects(Array.isArray(r.data) ? r.data : [])).catch(() => setProjects([]));
  }, []);

  const lockedProp = (Array.isArray(properties) ? properties : []).find(p => p.id === lockedPropertyId);

  const toggleRow = async (v) => {
    if (expandedId === v.id) { setExpandedId(null); setDetail(null); setEditMode(false); return; }
    setExpandedId(v.id);
    setDetail(null);
    setEditMode(false);
    setDetailWarning(null);
    setDetailLoading(true);
    try {
      const r = await api.get(`/violations/${v.id}`);
      setDetail(r.data);
    } catch (e) { toast.error('Could not load violation'); }
    setDetailLoading(false);
  };

  const refreshFdny = async (id) => {
    setRefreshingId(id);
    try {
      const r = await api.post(`/violations/${id}/fetch-fdny`);
      setViolations(prev => prev.map(v => (v.id === id ? r.data : v)));
      if (detail && detail.id === id) { setDetail(r.data); setDetailWarning(r.data?.warning || null); }
      if (r.data?.warning) toast.warning(r.data.warning);
      else toast.success('Refreshed from FDNY Open Data');
      fetchSummary();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not fetch FDNY data'); }
    setRefreshingId(null);
  };

  const refreshDob = async (id) => {
    setRefreshingId(id);
    try {
      const r = await api.post(`/violations/${id}/fetch-dob`);
      setViolations(prev => prev.map(v => (v.id === id ? r.data : v)));
      if (detail && detail.id === id) { setDetail(r.data); setDetailWarning(r.data?.warning || null); }
      if (r.data?.warning) toast.warning(r.data.warning);
      else toast.success('Verified against NYC DOB Open Data');
      fetchSummary();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not fetch DOB data'); }
    setRefreshingId(null);
  };

  // Auto-expand a violation when arriving via ?open=<id>
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId) {
      toggleRow({ id: openId });
      setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('open'); return p; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshAll = () => { fetchViolations(); fetchSummary(); };

  const openAdd = () => { setAddForm({ ...EMPTY_FORM, property_id: lockedPropertyId || '', project_id: lockedProjectId || '' }); setAddOpen(true); };

  // Candidates for "Link Existing": this property's violations not already on this project.
  const linkCandidates = (Array.isArray(violations) ? violations : []).filter(v => v.property_id === lockedPropertyId && !inProject(v, lockedProjectId));

  const linkSelected = async (ids) => {
    setLinking(true);
    try {
      await Promise.all(ids.map(id => {
        const v = linkCandidates.find(x => x.id === id);
        return api.put(`/violations/${id}`, buildProjectLinkPatch(v, lockedProjectId));
      }));
      toast.success(`Linked ${ids.length} violation${ids.length === 1 ? '' : 's'} to this project`);
      setLinkOpen(false);
      refreshAll();
    } catch (e) { toast.error('Could not link some violations'); }
    setLinking(false);
  };

  const createViolation = async () => {
    if (!addForm.property_id) return toast.error('Property is required');
    if (!addForm.violation_number.trim()) return toast.error('Violation number is required');
    if (!addForm.issuing_agency) return toast.error('Issuing agency is required');
    if (!addForm.issue_date) return toast.error('Issue date is required');
    if (!addForm.description.trim()) return toast.error('Description is required');
    setSaving(true);
    try {
      await api.post('/violations', { ...addForm, project_id: addForm.project_id || null });
      toast.success('Violation added');
      setAddOpen(false);
      setAddForm(EMPTY_FORM);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error adding violation'); }
    setSaving(false);
  };

  const startEdit = () => {
    setEditForm({
      property_id: detail.property_id || '', project_id: detail.project_id || '',
      violation_number: detail.violation_number || '', issuing_agency: detail.issuing_agency || '',
      issue_date: detail.issue_date || '', description: detail.description || '',
      remedy: detail.remedy || '', penalty_amount: detail.penalty_amount || 0,
      compliance_status: detail.compliance_status || 'Active', hearing_date: detail.hearing_date || '',
      resolution_date: detail.resolution_date || '', notes: detail.notes || '',
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    if (!editForm.violation_number.trim()) return toast.error('Violation number is required');
    if (!editForm.description.trim()) return toast.error('Description is required');
    setSaving(true);
    try {
      const r = await api.put(`/violations/${detail.id}`, { ...editForm, project_id: editForm.project_id || null });
      setDetail(r.data);
      setEditMode(false);
      toast.success('Violation updated');
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating violation'); }
    setSaving(false);
  };

  const deleteViolation = async (id) => {
    const ok = await confirm({ title: 'Delete violation', description: 'Delete this violation? This cannot be undone.', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await api.delete(`/violations/${id}`);
      toast.success('Violation deleted');
      setExpandedId(null);
      setDetail(null);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting violation'); }
  };

  const exportPdf = async () => {
    if (!propertyFilter) return;
    setExporting(true);
    try {
      const r = await api.get(`/violations/export-pdf?property_id=${propertyFilter}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const prop = (Array.isArray(properties) ? properties : []).find(p => p.id === propertyFilter);
      a.download = `Violation_Report_${(prop?.display_name || prop?.address || 'property').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error exporting PDF'); }
    setExporting(false);
  };

  const exportAllPdf = async () => {
    setExportingAll(true);
    try {
      const r = await api.get('/violations/export-pdf-all', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Portfolio_Violation_Report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error exporting portfolio report'); }
    setExportingAll(false);
  };

  const filtered = (Array.isArray(violations) ? violations : []).filter(v => {
    if (lockedProjectId && !inProject(v, lockedProjectId)) return false;
    if (propertyFilter && v.property_id !== propertyFilter) return false;
    if (agencyFilter !== 'All' && v.issuing_agency !== agencyFilter) return false;
    if (statusFilter !== 'All' && v.compliance_status !== statusFilter) return false;
    const q = search.toLowerCase();
    if (q && !((v.violation_number || '').toLowerCase().includes(q) || (v.description || '').toLowerCase().includes(q))) return false;
    return true;
  });

  const renderFields = (form, setForm, prefix) => {
    const propertyOpts = (Array.isArray(properties) ? properties : []).map(p => ({ value: p.id, label: `${p.display_name || p.address}: ${p.borough}` }));
    const projectOpts = (Array.isArray(projects) ? projects : []).filter(p => form.property_id && p.property_id === form.property_id).map(p => ({ value: p.id, label: `${p.project_number}: ${p.name}` }));
    return (
      <div className="space-y-3">
        {lockedPropertyId ? (
          <div>
            <Label className="text-xs text-muted-foreground">Property</Label>
            <Input value={lockedProp ? (lockedProp.display_name || lockedProp.address) : 'This property'} disabled className="bg-secondary/40 border-border" data-testid={`${prefix}-property-locked`} />
          </div>
        ) : (
          <div>
            <Label className="text-xs text-muted-foreground">Property *</Label>
            <SearchableSelect
              options={propertyOpts}
              value={form.property_id}
              onChange={(val) => setForm({ ...form, property_id: val, project_id: '' })}
              placeholder="Select property"
              searchPlaceholder="Search properties..."
              emptyText="No properties found."
              allowClear={false}
              testid={`${prefix}-property`}
            />
          </div>
        )}
        {!lockedProjectId && (
          <div>
            <Label className="text-xs text-muted-foreground">Project <span className="text-muted-foreground/60">(optional)</span></Label>
            <SearchableSelect
              options={projectOpts}
              value={form.project_id}
              onChange={(val) => setForm({ ...form, project_id: val })}
              placeholder={form.property_id ? 'Link a project (optional)' : 'Select a property first'}
              searchPlaceholder="Search projects..."
              emptyText={form.property_id ? 'No projects linked to this property.' : 'Select a property first.'}
              testid={`${prefix}-project`}
            />
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Violation Number *</Label>
            <Input value={form.violation_number} onChange={e => setForm({ ...form, violation_number: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-number`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Issuing Agency *</Label>
            <Select value={form.issuing_agency} onValueChange={v => setForm({ ...form, issuing_agency: v })}>
              <SelectTrigger className="bg-background border-border" data-testid={`${prefix}-agency`}><SelectValue placeholder="Select agency" /></SelectTrigger>
              <SelectContent className="bg-card border-border">{AGENCIES.map(a => <SelectItem key={a} value={a} className="text-sm">{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Issue Date *</Label>
            <Input type="date" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-issue-date`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Compliance Status</Label>
            <Select value={form.compliance_status} onValueChange={v => setForm({ ...form, compliance_status: v })}>
              <SelectTrigger className="bg-background border-border" data-testid={`${prefix}-status`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">{STATUSES.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Description *</Label>
          <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="bg-background border-border" data-testid={`${prefix}-description`} />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Remedy</Label>
          <Textarea value={form.remedy} onChange={e => setForm({ ...form, remedy: e.target.value })} rows={2} className="bg-background border-border" data-testid={`${prefix}-remedy`} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Penalty ($)</Label>
            <Input type="number" min="0" step="0.01" value={form.penalty_amount} onChange={e => setForm({ ...form, penalty_amount: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-penalty`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Hearing Date</Label>
            <Input type="date" value={form.hearing_date || ''} onChange={e => setForm({ ...form, hearing_date: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-hearing-date`} />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Resolution Date</Label>
            <Input type="date" value={form.resolution_date || ''} onChange={e => setForm({ ...form, resolution_date: e.target.value })} className="bg-background border-border" data-testid={`${prefix}-resolution-date`} />
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Notes</Label>
          <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-background border-border" data-testid={`${prefix}-notes`} />
        </div>
      </div>
    );
  };

  const renderDetailPanel = () => {
    if (detailLoading) return <p className="text-sm text-muted-foreground py-2">Loading violation...</p>;
    if (!detail) return null;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          {detail.issuing_agency === 'FDNY' && (
            fdnyPattern(detail) === 'A' ? (
              <Button size="sm" variant="outline" className="border-border" title="View on FDNY Business Portal (enforcement orders not in NYC Open Data)" onClick={() => window.open(FDNY_PORTAL_URL, '_blank')} data-testid="violation-fdny-portal-btn">
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> FDNY Portal
              </Button>
            ) : (
              <Button size="sm" variant="outline" className="border-border" disabled={refreshingId === detail.id} onClick={() => refreshFdny(detail.id)} data-testid="violation-fetch-fdny-btn">
                {refreshingId === detail.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Fetch FDNY
              </Button>
            )
          )}
          {!editMode && <Button size="sm" variant="outline" className="border-border" onClick={startEdit} data-testid="edit-violation-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>}
          {detail.issuing_agency === 'DOB' && (
            <Button size="sm" variant="outline" className="border-border" disabled={refreshingId === detail.id} onClick={() => refreshDob(detail.id)} data-testid="violation-fetch-dob-btn">
              {refreshingId === detail.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />} Refresh DOB
            </Button>
          )}
          <Button size="sm" variant="outline" className="text-destructive hover:text-destructive border-border" onClick={() => deleteViolation(detail.id)} data-testid="delete-violation-btn"><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
        </div>

        {editMode ? (
          <>
            {renderFields(editForm, setEditForm, 'edit-violation')}
            <div className="flex gap-2 mt-2">
              <Button onClick={saveEdit} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-violation-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
              <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
            </div>
          </>
        ) : (
          <div className="space-y-4" data-testid="violation-detail-view">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {[
                ['Violation #', detail.violation_number, true],
                ['Agency', detail.issuing_agency, false],
                ['Compliance Status', detail.compliance_status, false],
                ['Penalty Amount', fmtMoney(detail.penalty_amount), true],
                ['Issue Date', detail.issue_date, true],
                ['Hearing Date', detail.hearing_date, true],
                ['Resolution Date', detail.resolution_date, true],
              ].map(([label, val, mono]) => (
                <p key={label} className="text-sm">
                  <span style={{ color: '#031333' }}>{label}: </span>
                  <span style={{ color: '#010A21', fontFamily: mono ? "'DM Mono', monospace" : undefined }}>{val || '\u00A0'}</span>
                </p>
              ))}
              {!lockedPropertyId && (
                <p className="text-sm">
                  <span style={{ color: '#031333' }}>Property: </span>
                  {detail.property_id
                    ? <button onClick={() => navigate(`/properties/${detail.property_id}`)} className="text-[#1B4A76] hover:underline" data-testid="violation-detail-property-link">{detail.property_display_name || detail.property_address || 'View property'}</button>
                    : <span style={{ color: '#010A21' }}>-</span>}
                </p>
              )}
              <p className="text-sm">
                <span style={{ color: '#031333' }}>Project: </span>
                {detail.project_id
                  ? <button onClick={() => navigate(`/projects/${detail.project_id}`)} className="text-[#1B4A76] hover:underline" data-testid="violation-detail-project-link">{detail.project_number || detail.project_name || 'View project'}</button>
                  : <span style={{ color: '#010A21' }}>-</span>}
              </p>
            </div>
            {[['Description', detail.description], ['Remedy', detail.remedy], ['Notes', detail.notes]].map(([label, val]) => (
              <div key={label}>
                <p className="text-sm" style={{ color: '#031333' }}>{label}:</p>
                <p className="text-sm whitespace-pre-wrap" style={{ color: '#010A21' }}>{val || '\u00A0'}</p>
              </div>
            ))}
            {detail.issuing_agency === 'FDNY' && fdnyPattern(detail) === 'B' && (
              <p className="text-xs text-muted-foreground pt-1" data-testid="violation-last-fetched">
                {detail.last_fetched_at ? `Last fetched from FDNY: ${fmtDate(detail.last_fetched_at)}` : 'Never fetched from FDNY'}
              </p>
            )}
            {detail.issuing_agency === 'DOB' && (
              <p className="text-xs text-muted-foreground pt-1" data-testid="violation-dob-last-fetched">
                {detail.last_fetched_at ? `Last verified against DOB Open Data: ${fmtDate(detail.last_fetched_at)}` : 'Never verified against DOB Open Data'}
              </p>
            )}
            {detail.issuing_agency === 'FDNY' && fdnyPattern(detail) === 'A' && (
              <div className="rounded-md px-3 py-2" style={{ backgroundColor: '#FEF3C7', color: '#B45309' }} data-testid="violation-fdny-portal-notice">
                <p className="text-xs">FDNY enforcement order data is not available through NYC Open Data. Look up this violation on the FDNY Business Portal.</p>
                <a href={FDNY_PORTAL_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1 text-xs underline" data-testid="violation-fdny-portal-link"><ExternalLink className="h-3 w-3" /> Open FDNY Business Portal →</a>
              </div>
            )}
            {detailWarning && (
              <div className="flex items-start gap-2 rounded-md px-3 py-2" style={{ backgroundColor: '#FEF3C7', color: '#B45309' }} data-testid="violation-fdny-warning">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <p className="text-xs">{detailWarning}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const openCount = (lockedPropertyId || lockedProjectId)
    ? filtered.filter(v => v.compliance_status === 'Active' || v.compliance_status === 'Pending').length
    : (summary?.total_open ?? 0);

  return (
    <div className="space-y-4" data-testid="violations-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground" data-testid="violations-open-count">{openCount} open violations</p>
        <div className="flex items-center gap-2">
          {!lockedPropertyId && (
            <Button variant="outline" onClick={exportAllPdf} disabled={exportingAll} className="border-border" data-testid="export-all-violations-btn">
              <Download className="h-4 w-4 mr-1" /> {exportingAll ? 'Exporting...' : 'Export All Violations'}
            </Button>
          )}
          {propertyFilter ? (
            <Button variant="outline" onClick={exportPdf} disabled={exporting} className="border-border" data-testid="export-violations-pdf-btn">
              <Download className="h-4 w-4 mr-1" /> {exporting ? 'Exporting...' : 'Export PDF'}
            </Button>
          ) : (
            <span title="Filter by a property to export its violation report." data-testid="export-pdf-disabled-wrap">
              <Button variant="outline" disabled className="border-border" data-testid="export-violations-pdf-btn">
                <Download className="h-4 w-4 mr-1" /> Export PDF
              </Button>
            </span>
          )}
          <Button className="bg-primary text-primary-foreground" onClick={openAdd} data-testid="add-violation-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Violation
          </Button>
          {lockedProjectId && lockedPropertyId && (
            <Button variant="outline" className="border-border" onClick={() => setLinkOpen(true)} data-testid="link-existing-violations-btn">
              <Link2 className="h-4 w-4 mr-1" /> Link Existing
            </Button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        {!lockedPropertyId && (
          <div className="w-64">
            <SearchableSelect
              options={(Array.isArray(properties) ? properties : []).map(p => ({ value: p.id, label: `${p.display_name || p.address}: ${p.borough}` }))}
              value={propertyFilter}
              onChange={setPropertyFilter}
              placeholder="All Properties"
              searchPlaceholder="Search properties..."
              emptyText="No properties found."
              testid="violations-property-filter"
            />
          </div>
        )}
        <Select value={agencyFilter} onValueChange={setAgencyFilter}>
          <SelectTrigger className="w-36 bg-card border-border text-sm" data-testid="violations-agency-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="All" className="text-sm">All Agencies</SelectItem>
            {AGENCIES.map(a => <SelectItem key={a} value={a} className="text-sm">{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-card border-border text-sm" data-testid="violations-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="All" className="text-sm">All Statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search violations..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border" data-testid="violations-search" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-transparent">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs text-muted-foreground">Violation #</TableHead>
              <TableHead className="text-xs text-muted-foreground">Agency</TableHead>
              {!lockedPropertyId && <TableHead className="text-xs text-muted-foreground">Property</TableHead>}
              <TableHead className="text-xs text-muted-foreground">Issue Date</TableHead>
              <TableHead className="text-xs text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs text-muted-foreground text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(v => (
              <Fragment key={v.id}>
                <TableRow
                  className={`group border-border cursor-pointer ${expandedId === v.id ? 'bg-secondary/40' : 'hover:bg-secondary/30'}`}
                  onClick={() => toggleRow(v)}
                  data-testid={`violation-row-${v.id}`}
                  style={{ height: '40px' }}
                >
                  <TableCell className="text-sm" style={{ fontFamily: "'DM Mono', monospace" }}>
                    <span className="inline-flex items-center gap-1.5">
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground transition-transform" style={{ transform: expandedId === v.id ? 'rotate(90deg)' : 'none' }} />
                      {v.violation_number}
                    </span>
                  </TableCell>
                  <TableCell><AgencyDot agency={v.issuing_agency} /></TableCell>
                  {!lockedPropertyId && (
                    <TableCell className="text-sm">
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/properties/${v.property_id}`); }} className="text-[#1B4A76] hover:underline text-left" data-testid={`violation-property-link-${v.id}`}>
                        {v.property_display_name || v.property_address || '\u00A0'}
                      </button>
                    </TableCell>
                  )}
                  <TableCell className="text-sm text-muted-foreground" style={{ fontFamily: "'DM Mono', monospace" }}>{v.issue_date}</TableCell>
                  <TableCell><ViolationStatusDot status={v.compliance_status} /></TableCell>
                  <TableCell className="text-right">
                    {v.issuing_agency === 'FDNY' && (
                      <div className="flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                        {fdnyPattern(v) === 'A' ? (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-primary" title="View on FDNY Business Portal (enforcement orders not in NYC Open Data)" onClick={() => window.open(FDNY_PORTAL_URL, '_blank')} data-testid={`violation-fdny-portal-${v.id}`}>
                            <ExternalLink className="h-3.5 w-3.5" />
                            <span className="ml-1">FDNY Portal</span>
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-primary" disabled={refreshingId === v.id} onClick={() => refreshFdny(v.id)} data-testid={`violation-fetch-fdny-${v.id}`}>
                            {refreshingId === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                            <span className="ml-1">Fetch FDNY</span>
                          </Button>
                        )}
                      </div>
                    )}
                    {v.issuing_agency === 'DOB' && (
                      <div className="flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-primary" disabled={refreshingId === v.id} onClick={() => refreshDob(v.id)} data-testid={`violation-fetch-dob-${v.id}`}>
                          {refreshingId === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                          <span className="ml-1">Refresh DOB</span>
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
                <tr data-testid={`violation-expand-${v.id}`}>
                  <td colSpan={lockedPropertyId ? 5 : 6} style={{ padding: 0, border: 0 }}>
                    <div style={{ maxHeight: expandedId === v.id ? '2000px' : '0', overflow: 'hidden', transition: 'max-height 200ms ease-out' }}>
                      {expandedId === v.id && (
                        <div className="px-4 py-4 bg-secondary/20 border-b border-border" data-testid={`violation-detail-${v.id}`}>
                          {renderDetailPanel()}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              </Fragment>
            ))}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={lockedPropertyId ? 5 : 6} className="text-center text-sm text-muted-foreground py-8">No violations found.</TableCell></TableRow>
            )}
            {loading && (
              <TableRow><TableCell colSpan={lockedPropertyId ? 5 : 6} className="text-center text-sm text-muted-foreground py-8">Loading violations...</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Violation Sheet */}
      <Sheet open={addOpen} onOpenChange={setAddOpen}>
        <SheetContent className="sm:max-w-md bg-card border-border overflow-y-auto" data-testid="add-violation-sheet">
          <SheetHeader>
            <SheetTitle>Add Violation</SheetTitle>
            <SheetDescription>Record a new violation against a property.</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {renderFields(addForm, setAddForm, 'add-violation')}
            <div className="flex gap-2 mt-5">
              <Button onClick={createViolation} disabled={saving} className="bg-primary text-primary-foreground" data-testid="submit-add-violation">{saving ? 'Saving...' : 'Add Violation'}</Button>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <LinkExistingDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        title="Link Existing Violations"
        description="Select violations recorded on this property to also track under this project."
        records={linkCandidates}
        renderPrimary={(v) => v.violation_number}
        renderSecondary={(v) => `${v.issuing_agency || ''}${v.description ? ` · ${v.description}` : ''}`}
        linking={linking}
        onConfirm={linkSelected}
        emptyText="No unlinked violations found for this property."
        testid="link-existing-violation"
      />
    </div>
  );
}
